import React, { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { ng } from "../../ngds";
import { SvgCurve, SvgTxt } from "./draw/DrawMtrlCurve";
import ConcCurve from "./draw/ConcCurve";
import SteelCurve from "./draw/SteelCurve";
import TimberCurve from "./draw/TimberCurve";
import ReinfCurve from "./draw/ReinfCurve";

export interface TooltipPosition {
  clientX: number;
  clientY: number;
}

export type MaterialRecord = Record<string, any>;

export interface MaterialSvg {
  w: number;
  h: number;
  origo: {
    x: number;
    y: number;
  };
  max: {
    x: number;
    y: number;
  };
  pad: number;
}

export interface IMaterial {
  id: string;
  name: string;
  mtrl: ng.material.Data | undefined;
}

interface DrawMtrlCurveProps {
  mtrl: MaterialRecord;
  svg: MaterialSvg;
}

export const Tooltip = (props: { tooltipPosition: TooltipPosition | null; hoverdMtrl: IMaterial | null }) => {
  const { hoverdMtrl } = props;
  if (!hoverdMtrl || !props.tooltipPosition) return null;

  const tooltipStyle: React.CSSProperties = {
    position: "fixed",
    top: props.tooltipPosition.clientY,
    left: props.tooltipPosition.clientX + 220,
    visibility: "visible",
    borderRadius: 8,

    backgroundColor: "white",
    border: "1px solid lightgrey",
    boxShadow: "rgba(0, 0, 0, 0.1) 0px 5px 15px 0px",
    zIndex: 9999999,
    padding: 20,
  };

  const w = 400;
  const h = 400;
  const pad = 0.1;
  if (!hoverdMtrl.mtrl?.getReinforcementData) {
    return;
  }
  const units = hoverdMtrl.mtrl?.getReinforcementData()?.properties;
  let tmpmtrl: MaterialRecord | null = null;
  if (units) {
    tmpmtrl = {
      materialCategory: "MTRL_REINFORCEMENT",
      units: units,
      ...hoverdMtrl,
    };
  }
  const mtrlSVG: MaterialSvg = {
    w: w,
    h: h,
    origo: { x: w * pad, y: h * (1 - pad) },
    max: { x: w * (1 - 2.5 * pad), y: h * (1 - 2.5 * pad) },
    pad: pad,
  };

  return createPortal(
    <div style={tooltipStyle}>
      {tmpmtrl && units ? (
        <div style={{ display: "flex", alignItems: "flex-start", width: 280 }}>
          <DrawMtrlCurve mtrl={tmpmtrl} svg={mtrlSVG} />
        </div>
      ) : (
        <div style={{ color: "#666", fontSize: 12 }}>{hoverdMtrl.name}</div>
      )}
    </div>,
    document.body,
  );
};

const DrawMtrlCurve = (props: DrawMtrlCurveProps) => {
  const { mtrl, svg } = props;
  const vb = "0 0 " + svg.w.toString() + " " + svg.h.toString();
  return (
    <svg viewBox={vb}>
      {mtrl.hasOwnProperty("materialCategory") && (
        <>
          <Axes mtrl={mtrl} svg={svg} />
          <Curve mtrl={mtrl} svg={svg} />
        </>
      )}
    </svg>
  );
};

function Axes(props: DrawMtrlCurveProps) {
  const { svg } = props;

  const xCoord = [(svg.w * (1 - svg.pad)).toString(), (svg.w * svg.pad).toString()];
  const yCoord = [(svg.h * (1 - svg.pad)).toString(), (svg.h * svg.pad).toString()];

  const xPnt = xCoord[0] + "," + yCoord[0];
  const yPnt = xCoord[1] + "," + yCoord[1];

  const xArrow = "l-10,-5 l0,10 l10,-5";
  const yArrow = "l-5,10 l10,0 l-5,-10";

  const xTxt = [(svg.w * (1 - svg.pad) + 5).toString(), (svg.h * (1 - svg.pad) + 7).toString()];
  const yTxt = [(svg.w * svg.pad - 7).toString(), (svg.h * svg.pad - 5).toString()];

  let xAxis = "M" + svg.origo.x.toString() + "," + svg.origo.y.toString() + " " + "L" + xPnt + " " + xArrow;
  let yAxis = "M" + svg.origo.x.toString() + "," + svg.origo.y.toString() + " " + "L" + yPnt + " " + yArrow;

  return (
    <>
      {/* <rect
          width={svg.w}
          height={svg.h}
          style={{
            fill: "rgb(255,255,255)",
            strokeWidth: "2",
            stroke: "rgb(0,0,0)",
          }}
        />
*/}
      <path id="X-axis" fill="black" stroke="black" strokeWidth="1" d={xAxis} />

      <text id="x-label" x={xTxt[0]} y={xTxt[1]} style={{ fontSize: "28px" }}>
        &epsilon;
      </text>

      <path id="Y-axis" fill="black" stroke="black" strokeWidth="1" d={yAxis} />

      <text id="y-label" x={yTxt[0]} y={yTxt[1]} style={{ fontSize: "28px" }}>
        &sigma;
      </text>
    </>
  );
}

function Curve(props: DrawMtrlCurveProps) {
  const { mtrl, svg } = props;

  let curves: SvgCurve[] = [];
  let txts: SvgTxt[] = [];

  switch (mtrl["materialCategory"]) {
    default:
    case "MTRL_CONCRETE": {
      [curves, txts] = ConcCurve(mtrl, svg);

      break;
    }

    case "MTRL_STEEL": {
      [curves, txts] = SteelCurve(mtrl, svg);

      break;
    }

    case "MTRL_TIMBER": {
      [curves, txts] = TimberCurve(mtrl, svg);
      break;
    }

    case "MTRL_REINFORCEMENT": {
      [curves, txts] = ReinfCurve(mtrl, svg);
      break;
    }
  }

  return (
    <>
      {curves.map((curve, index) => {
        return (
          <path
            id={"curve-" + index}
            key={"path-" + index}
            fill="none"
            stroke={curve.stroke}
            strokeWidth={curve.width}
            strokeDasharray={curve.dash}
            d={curve.data}
          />
        );
      })}

      {txts.map((txt, index) => {
        return (
          <text id={"curve-txt-" + index} key={"curve-txt-" + index} x={txt.x} y={txt.y} style={txt.style}>
            {ConvertStr2Html(txt.text).documentElement.outerText}
          </text>
        );
      })}
    </>
  );
}

const ConvertStr2Html = (txt: string) => {
  const parser = new DOMParser();
  const tmp = parser.parseFromString(txt, "text/html");

  return tmp;
};
